import type { SystemStyleObject } from "@mui/system";
import { actionBtn } from "../../components/ui/ActionButton/styles";
import { questionCardSurface } from "../../components/ui/QuestionSurface/styles";
import type { ThemeV2 } from "../../util/types";

const selectedDisabledStyle = (
  theme: ThemeV2,
  selected: boolean,
): SystemStyleObject =>
  selected
    ? {
        "&:disabled": {
          borderColor: theme.tokens.color.action.primary,
          backgroundColor: theme.tokens.color.action.primary,
          color: theme.tokens.color.action.primaryText,
          cursor: "not-allowed",
          "&:hover": {
            backgroundColor: theme.tokens.color.action.primary,
            color: theme.tokens.color.action.primaryText,
          },
        },
      }
    : {};

export const compactGridListStyle = (theme: ThemeV2): SystemStyleObject => ({
  display: "flex",
  flexDirection: "column",
  gap: `${theme.tokens.spacing["3"]}px`,
  width: "100%",
  minWidth: 0,
  padding: `${theme.tokens.spacing["2"]}px`,
});

export const compactGridCardStyle = (theme: ThemeV2): SystemStyleObject => ({
  display: "flex",
  flexDirection: "column",
  gap: `${theme.tokens.spacing["3"]}px`,
  minWidth: 0,
  padding: `${theme.tokens.spacing["3"]}px`,
  ...questionCardSurface(theme),
});

export const compactGridButtonStyle = (
  theme: ThemeV2,
  selected: boolean,
): SystemStyleObject => ({
  ...actionBtn(theme, selected, {}),
  width: "100%",
  minWidth: 0,
  minHeight: `${theme.tokens.spacing["5"] * 2}px`,
  height: "100%",
  margin: 0,
  boxShadow: "none",
  ...selectedDisabledStyle(theme, selected),
});

export const wideGridButtonStyle = (
  theme: ThemeV2,
  selected: boolean,
): SystemStyleObject => ({
  ...actionBtn(theme, selected, {}),
  width: "100%",
  minWidth: 0,
  minHeight: `${theme.tokens.spacing["5"] * 3}px`,
  margin: 0,
  border: 0,
  borderRadius: 0,
  boxShadow: "none",
  ...selectedDisabledStyle(theme, selected),
});

export const compactGridInputStyle = (theme: ThemeV2): SystemStyleObject => ({
  border: `1px solid ${theme.tokens.color.action.primary}`,
  borderRadius: `${theme.tokens.shape.radius.md}px`,
  backgroundColor: theme.tokens.color.surface.card,
});

export const wideGridInstrumentStyle = (
  theme: ThemeV2,
  showGridLines = theme.show_grid_lines,
  grouped = false,
): SystemStyleObject => ({
  backgroundColor: theme.tokens.color.surface.card,
  border: `1px solid ${
    showGridLines
      ? theme.tokens.color.action.primary
      : theme.tokens.color.border.default
  }`,
  borderRadius: `${theme.tokens.shape.radius.lg}px`,
  padding: `${theme.tokens.spacing["4"]}px`,
  "& thead": {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: theme.tokens.color.surface.card,
  },
  "& thead th": {
    backgroundColor: theme.tokens.color.surface.card,
  },
  ...(grouped && {
    "& th, & td": { border: `1px solid ${theme.tokens.color.action.primary}` },
  }),
});

export const wideGridTableStyle: SystemStyleObject = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  borderCollapse: "collapse",
  tableLayout: "fixed",
};

export const wideGridViewportStyle: SystemStyleObject = {
  width: "100%",
  minWidth: 0,
};

export const wideGridRowHeaderStyle = (
  theme: ThemeV2,
  showGridLines = theme.show_grid_lines,
): SystemStyleObject => ({
  width: "15%",
  paddingRight: `${theme.tokens.spacing["4"]}px`,
  textAlign: "right",
  verticalAlign: "middle",
  fontWeight: theme.tokens.typography.weight.regular,
  color: theme.tokens.color.action.primary,
  ...(showGridLines && {
    border: `1px solid ${theme.tokens.color.action.primary}`,
    padding: `${theme.tokens.spacing["2"]}px`,
  }),
});

export const wideGridColumnHeaderStyle = (
  theme: ThemeV2,
  showGridLines = theme.show_grid_lines,
): SystemStyleObject => ({
  padding: `${theme.tokens.spacing["2"]}px`,
  textAlign: "center",
  fontWeight: theme.tokens.typography.weight.regular,
  color: theme.tokens.color.action.primary,
  overflowWrap: "anywhere",
  wordBreak: "normal",
  ...(showGridLines && {
    border: `1px solid ${theme.tokens.color.action.primary}`,
  }),
});

export const wideGridCellStyle = (theme: ThemeV2): SystemStyleObject => ({
  border: `1px solid ${theme.tokens.color.action.primary}`,
  padding: 0,
  height: `${theme.tokens.spacing["5"] * 3}px`,
  textAlign: "center",
  verticalAlign: "middle",
});

export const optionalGridLineStyle = (
  theme: ThemeV2,
  showGridLines = theme.show_grid_lines,
): SystemStyleObject =>
  showGridLines
    ? { border: `1px solid ${theme.tokens.color.action.primary}` }
    : {};

export const optionalGridScaleInsetStyle = (
  theme: ThemeV2,
  compact = false,
  showGridLines = theme.show_grid_lines,
): SystemStyleObject =>
  showGridLines && !compact
    ? { paddingInline: `${theme.tokens.spacing["4"]}px` }
    : {};

export const optionalGridFlushCellStyle = (
  theme: ThemeV2,
  showGridLines = theme.show_grid_lines,
): SystemStyleObject => (showGridLines ? { padding: 0 } : {});
